import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: October 13, 2024
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4">
                  This Privacy Notice for Asha Dental, Inc. (doing business as Asha Dental) ("we," "us," or "our"), 
                  describes how and why we might access, collect, store, use, and/or share ("process") your personal 
                  information when you use our services ("Services"), including when you:
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Visit our website at patient.asha.health, or any website of ours that links to this Privacy Notice
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and 
                  choices. We are responsible for making decisions about how your personal information is processed. If 
                  you do not agree with our policies and practices, please do not use our Services. If you still have any 
                  questions or concerns, please contact us at info@asha.health.
                </p>
              </div>

              {/* Summary Section */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">SUMMARY OF KEY POINTS</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This summary provides key points from our Privacy Notice, but you can find out more details about any 
                  of these topics by clicking the link following each key point or by using our table of contents below to 
                  find the section you are looking for.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">What personal information do we process?</p>
                    <p className="text-gray-700 leading-relaxed">
                      When you visit, use, or navigate our Services, we may process personal information depending on how you 
                      interact with us and the Services, the choices you make, and the products and features you use.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-900 mb-2">Do we process any sensitive personal information?</p>
                    <p className="text-gray-700 leading-relaxed">
                      Some of the information may be considered "special" or "sensitive" in certain jurisdictions. We process 
                      health information as necessary for our dental practice management services.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-900 mb-2">Do we collect any information from third parties?</p>
                    <p className="text-gray-700 leading-relaxed">
                      We may collect information from public databases, marketing partners, social media platforms, and other 
                      outside sources.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-900 mb-2">How do we process your information?</p>
                    <p className="text-gray-700 leading-relaxed">
                      We process your information to provide, improve, and administer our Services, communicate with you, for 
                      security and fraud prevention, and to comply with law.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">1. What Information Do We Collect?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We collect personal information that you voluntarily provide to us when you register on the Services, 
                  express an interest in obtaining information about us or our products and Services, when you participate 
                  in activities on the Services, or otherwise when you contact us.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The personal information that we collect depends on the context of your interactions with us and the Services, 
                  the choices you make, and the products and features you use. The personal information we collect may include 
                  the following: names, email addresses, phone numbers, mailing addresses, billing addresses, and health information 
                  related to dental care.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">2. How Do We Process Your Information?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We process your personal information for a variety of reasons, depending on how you interact with our Services, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>To facilitate account creation and authentication</li>
                  <li>To deliver and facilitate delivery of services to the user</li>
                  <li>To respond to user inquiries and offer support</li>
                  <li>To send administrative information to you</li>
                  <li>To fulfill and manage your appointments and bookings</li>
                  <li>To enable user-to-user communications with your consent</li>
                  <li>To request feedback and contact you about your use of our Services</li>
                  <li>To protect our Services and prevent fraud</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">3. When and With Whom Do We Share Your Personal Information?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may share information in specific situations and with specific third parties, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Business partners to offer you certain products, services, or promotions</li>
                  <li>Service providers who help us provide our Services</li>
                  <li>Healthcare providers involved in your care</li>
                  <li>Insurance companies for billing and claims processing</li>
                  <li>Legal and regulatory authorities when required by law</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">4. How Long Do We Keep Your Information?</h3>
                <p className="text-gray-700 leading-relaxed">
                  We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice 
                  unless otherwise required by law. We will retain and use your information to the extent necessary to comply 
                  with our legal obligations, resolve disputes, and enforce our agreements. Healthcare records are maintained 
                  in accordance with applicable state and federal regulations.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">5. How Do We Keep Your Information Safe?</h3>
                <p className="text-gray-700 leading-relaxed">
                  We have implemented appropriate technical and organizational security measures designed to protect the security 
                  of any personal information we process. However, despite our safeguards and efforts to secure your information, 
                  no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure. 
                  We use industry-standard encryption and security protocols to protect your data.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">6. What Are Your Privacy Rights?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>The right to access – You have the right to request copies of your personal information</li>
                  <li>The right to rectification – You have the right to request correction of inaccurate information</li>
                  <li>The right to erasure – You have the right to request deletion of your personal information</li>
                  <li>The right to restrict processing – You have the right to request restriction of processing</li>
                  <li>The right to data portability – You have the right to request transfer of your information</li>
                  <li>The right to object to processing – You have the right to object to our processing</li>
                </ul>
              </div>

              {/* Section 7 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">7. HIPAA Compliance</h3>
                <p className="text-gray-700 leading-relaxed">
                  As a healthcare technology provider, we are committed to compliance with the Health Insurance Portability and 
                  Accountability Act (HIPAA). We implement appropriate administrative, physical, and technical safeguards to protect 
                  the privacy and security of protected health information (PHI). Our business associate agreements with dental 
                  practices ensure that PHI is handled in accordance with HIPAA regulations.
                </p>
              </div>

              {/* Section 8 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">8. Do We Make Updates to This Notice?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes, we will update this notice as necessary to stay compliant with relevant laws. We may update this Privacy 
                  Notice from time to time. The updated version will be indicated by an updated "Last updated" date and the updated 
                  version will be effective as soon as it is accessible.
                </p>
              </div>

              {/* Section 9 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">9. How Can You Contact Us?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions or comments about this notice, you may email us at:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-gray-900 mb-2">Asha Dental, Inc.</p>
                  <p className="text-gray-700">Email: info@asha.health</p>
                  <p className="text-gray-700">Website: www.asha.health</p>
                </div>
              </div>

              {/* Section 10 */}
              <div>
                <h3 className="text-2xl font-bold mb-4">10. How Can You Review, Update, or Delete Your Data?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Based on the applicable laws of your country, you may have the right to request access to the personal information 
                  we collect from you, change that information, or delete it. To request to review, update, or delete your personal 
                  information, please contact us at info@asha.health.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
